<template>
  <div class="certificate-verified">
    <nav class="certificate-verified__nav-header">
      <router-link
        :to="{ name: 'home' }"
        class="certificate-verified__nav-header-home"
      >
        <img
          class="certificate-verified__nav-header-ine-logo"
          src="@/assets/images/home-logo.png"
          alt="INE Logo"
        />
      </router-link>
    </nav>
    <div v-if="!loading" ref="wrapper" class="certificate-verified__wrapper">
      <div class="certificate-verified__header">
        <div class="certificate-verified__header-top">
          <span class="pill pill--success certificate-verified__pill"
            >VERIFIED</span
          >
          <div class="certificate-verified__header-links">
            <button
              v-tooltip="{
                content: tooltipText,
                placement: 'top-center',
                hideOnTargetClick: false,
              }"
              class="btn btn--secondary certificate-verified__title-link"
              @click.stop="copyToClipboard"
              @mouseout="tooltipCopied = false"
            >
              <LinkIcon />
            </button>
            <a
              v-if="isOwner"
              v-tooltip="{
                content: 'Share on Linkedin',
                placement: 'top-center',
                hideOnTargetClick: false,
              }"
              :href="linkedInUrl"
              target="_blank"
              class="btn btn--secondary certificate-verified__title-link"
            >
              <LinkedinIcon />
            </a>
            <div
              v-if="isDownloadingPdf"
              class="certificate-verified__title-link certificate-verified__title-link--loader"
            >
              <ActionLoader :size="0.5" />
            </div>
            <a
              v-else
              v-tooltip="{
                content: 'Download',
                placement: 'top-center',
                hideOnTargetClick: false,
              }"
              class="btn btn--secondary certificate-verified__title-link"
              target="_blank"
              @click="downloadPdf"
            >
              <DownloadIcon />
            </a>
          </div>
        </div>
        <div class="certificate-verified__title-wrapper">
          <h2 class="certificate-verified__title">
            {{ certificate.certification_name }}
          </h2>
        </div>
        <div class="certificate-verified__certificate-description">
          {{ fullName }} has been awarded the
          {{ certificate.certification_name }}.
          <div
            class="certificate-verified__certificate-description-certificate"
            v-html="certificate.certificate_description_html"
          ></div>
        </div>
      </div>
      <Certificate />
      <div class="certificate-verified__footer">
        <h4 class="certificate-verified__footer-title">INE Certifications</h4>
        <p class="certificate-verified__footer-subtitle">
          Trusted by HR departments around the world, our certifications are
          scenario-based exams that prove your skills in the job market.
        </p>
        <router-link
          :to="{ name: 'certifications' }"
          class="btn btn--primary certificate-verified__footer-btn"
          >Learn more</router-link
        >
      </div>
    </div>
    <div v-if="loading" class="all-courses__loader-wrapper">
      <LoadingSpinner />
    </div>
  </div>
</template>

<script>
import CONSTANTS from '@/constants'
import { mapState } from 'vuex'
import { LinkIcon, LinkedinIcon, DownloadIcon } from 'vue-feather-icons'
import LoadingSpinner from '@/components/loaders/LoadingSpinner.vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import Certificate from '@/components/certificates/Certificate.vue'

const { SHARE_URL, ORGANIZATION_ID } = CONSTANTS.LINKEDIN

export default {
  name: 'CertificateVerified',
  components: {
    LinkIcon,
    LinkedinIcon,
    DownloadIcon,
    LoadingSpinner,
    ActionLoader,
    Certificate,
  },
  data: () => {
    return {
      qrCode: null,
      loading: false,
      tooltipCopied: false,
      isDownloadingPdf: false,
    }
  },
  computed: {
    ...mapState({
      certificate: state => state.certifications.currentCertificate,
      userProfile: state => state.auth.userProfile,
    }),
    isOwner() {
      return this.certificate.user_id === this.userProfile.uaaId
    },
    fullName() {
      return `${this.certificate.first_name} ${this.certificate.last_name}`
    },
    certificateURL() {
      return `${window.location.origin}/certificate/${this.certificate.attempt_id}`
    },
    tooltipText() {
      return this.tooltipCopied ? 'Link copied' : 'Copy link'
    },
    linkedInUrl() {
      const certId = this.certificate.attempt_id?.split('-')[0]
      if (this.certificate.finished_at) {
        const finishedAt = new Date(this.certificate.finished_at)
        return `${SHARE_URL}?startTask=CERTIFICATION_NAME&name=${
          this.certificate.certification_name
        }&organizationId=${ORGANIZATION_ID}&issueYear=${finishedAt.getFullYear()}&issueMonth=${finishedAt.getMonth()}&certId=${certId}&certUrl=${
          this.certificateURL
        }`
      }
      return `${SHARE_URL}?startTask=CERTIFICATION_NAME&name=${this.certificate.certification_name}&organizationId=${ORGANIZATION_ID}&certId=${certId}&certUrl=${this.certificateURL}`
    },
  },
  async mounted() {
    this.loading = true
    const attemptId = this.$route.params.attemptId
    if (!attemptId) {
      this.$router.push({
        name: 'NotFound',
        params: { pathMatch: this.$route.path },
      })
      throw new Error('NotFound')
    }
    try {
      await this.$store.dispatch('certifications/getCertificate', attemptId)
    } catch (error) {
      this.$router.push({
        name: 'NotFound',
        params: { pathMatch: this.$route.path },
      })
    }
    this.loading = false
    await this.$nextTick()
  },
  methods: {
    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.certificateURL)
        this.tooltipCopied = true
      } catch (err) {
        this.$store.dispatch(
          'app/setError',
          "Your browser doesn't allow clipboard access."
        )
      }
    },
    async downloadPdf() {
      this.isDownloadingPdf = true
      await this.$store.dispatch(
        'certifications/getPdfCertificate',
        this.certificate
      )
      this.isDownloadingPdf = false
    },
  },
}
</script>

<style lang="scss" scoped>
.certificate-verified {
  padding: 0px 25px 50px 25px;
  color: var(--white);
  position: relative;
  background-image: url('../assets/images/backgrounds/dashboard_bg.jpg');
  background-size: cover;
  background-repeat: no-repeat;
  width: 100%;
  height: 100%;
  z-index: 1000;
  &__nav-header {
    margin-bottom: 37px;
    display: flex;
    justify-content: space-between;

    @include tablet {
      margin-bottom: 60px;
    }

    &-home {
      margin: auto 0;
    }

    &-btns {
      margin: auto 0;
    }

    &-ine-logo {
      height: 28px;
      margin-top: 18px;
    }
  }

  &__nav-footer-btns {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    height: 56px;
    background-color: var(--dark-brighter);
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;

    max-width: 1400px;
    margin: 0 auto;
  }

  &__header {
    display: flex;
    flex-direction: column;
    width: 335px;
    margin-bottom: 30px;
    @include mobile {
      width: 725px;
    }
    @include tablet {
      width: 100%;
      max-width: 1073px;
    }
  }

  &__header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 100%;
    margin-bottom: 10px;
    @include mobile {
      margin-bottom: 0;
    }
  }
  &__header-links {
    display: flex;
    gap: 10px;
  }
  &__pill {
    padding: 9px 14px;
    height: 32px;
    @include mobile {
      margin-bottom: 26px;
    }
  }
  &__title-wrapper {
    display: flex;
    flex-direction: column;
  }

  &__title {
    font-weight: bold;
    font-size: rem(32);
    line-height: 38px;
    font-family: var(--font-bold);
    margin: 0 0 10px 0px;
    align-self: flex-start;
    @include mobile {
      font-size: rem(40);
      line-height: 52px;
    }
    @include tablet {
      font-size: rem(40);
      line-height: 52px;
    }
    &-wrapper {
      display: flex;
      justify-content: flex-start;
      align-items: center;
    }

    &-link {
      height: auto;
      padding: 12px;
      display: flex;
      justify-content: center;
      align-content: center;
      @include mobile {
        height: initial;
      }
      svg {
        fill: var(--btn-bg-color) !important;
        width: 14.5px;
        height: 14.5px;
        @include mobile {
          width: 21px;
          height: 21px;
        }
      }
      &--loader {
        padding: 4px 3.5px;
      }
    }
    &-verified {
      align-self: flex-start;
      background: var(--positive-light);
      color: var(--charcoal);
      width: 80px;
      height: 32px;
      border-radius: 20px;
      display: flex;
      align-items: center;
      text-transform: uppercase;
      font-family: var(--font-bold);
      font-size: rem(12);
      justify-content: space-around;
    }
  }
  &__certificate-description {
    font-size: rem(16);
    line-height: 24px;
    &-certificate {
      color: var(--light-blue-gray);
      display: inline;
      ::v-deep p {
        margin: 0;
        display: inline;
      }
    }

    @include tablet {
      font-size: rem(16);
      line-height: 24px;
    }
  }
  &__badge {
    height: 90px;
    position: absolute;
    right: 0;
    top: -10px;
    @include tablet {
      height: 200px;
      top: -30px;
    }
    @include desktop {
      height: 250px;
    }
    img {
      height: 100%;
    }
  }

  &__badge:after {
    content: '';
    width: 15px;
    height: 15px;
    position: absolute;
    top: 50%;
    left: 50%;
    box-shadow: -1px 1px 100px 60px rgb(51 51 51 / 50%);
    border-radius: 50%;

    @include tablet {
      width: 30px;
      height: 30px;
      box-shadow: -1px 1px 150px 120px rgb(51 51 51 / 50%);
    }
  }
  &__footer {
    width: 100%;
    text-align: center;
    margin-bottom: 50px;
    max-width: 354px;
    @include mobile {
      margin-bottom: unset;
      max-width: 725px;
    }
    &-title {
      font-weight: bold;
      font-size: rem(24);
      line-height: 32px;
      text-align: center;
      margin: 20px 0 10px;
      @include tablet {
        font-size: rem(32);
        line-height: 38px;
        margin: 45px 0 20px;
      }
    }
    &-subtitle {
      font-size: rem(16);
      line-height: 24px;
      text-align: center;
      color: var(--light-blue-gray);
      margin: 20px 0px;

      @include tablet {
        font-size: rem(24);
        line-height: 32px;
      }
    }
    &-btn {
      height: 36px;
      width: 100%;
      @include mobile {
        width: fit-content;
        height: 44px;
      }
    }
  }
}
img {
  position: relative;
  z-index: 10;
}
</style>
